// poApi.ts — the ONE place every purchase-order API-shape assumption lives.
//
// ERP expansion chunk 1, phase 6 (frontend scaffold). The backend
// (api/src/procurement/purchase-orders-router.js + purchase-orders-service.js)
// was built in parallel; the shapes below are read directly off that code, not
// guessed from the spec. If the backend contract shifts before Deploy B ships,
// this file is the only place that needs to change.
//
// Money is ALWAYS integer cents from the API (subtotal_cents, unit_cost_cents,
// line_total_cents). formatCents() is the one place that turns cents into a
// dollar string — no page should do `cents / 100` itself.
//
// Dates: order_date / ship_date / expected_date / expected_arrival_date are
// DATE columns (YYYY-MM-DD) — format with formatEventDate from date-utils.
// created_at / submitted_at / approved_at / closed_at are timestamptz — format
// with formatTimestamp. Never `new Date(apiField)` directly (see web/CLAUDE.md).

import { useApi } from '../apiHelpers';
import type { PaymentClock, PaymentTermsBasis } from './paymentClock';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type PoStatus =
  | 'draft'
  | 'pending_approval'
  | 'submitted'
  | 'partially_received'
  | 'received'
  | 'closed'
  | 'cancelled';

export type PoLineStatus = 'pending' | 'partial' | 'received' | 'cancelled';

export type PoLineType = 'stock' | 'service' | 'charge';

export type PoOrigin = 'app' | 'backfill' | 'pdf_import';

export type PoLineSource = 'procurement_hub' | 'manual';

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------

/** Vendor summary nested in GET /purchase-orders/:id — NOT the same shape as
 * the flat vendor_name/vendor_code/vendor_type columns on the list row below;
 * the two endpoints project the vendor differently and both are real. */
export interface PoVendorSummary {
  id: string;
  name: string;
  code: string | null;
  type: string;
  payment_terms: string | null;
  /** The vendor's CURRENT default payment clock (migration 078), beside the
   *  order's own snapshot in terms_days / terms_basis. The two differ once the
   *  order was edited or the vendor toggled after the order was cut. */
  payment_terms_days: number | null;
  payment_terms_basis: PaymentTermsBasis | null;
  on_hold: boolean;
  hold_reason: string | null;
  release_date: string | null; // YYYY-MM-DD
  is_disabled: boolean;
}

// Fields shared by the list row and the detail header.
interface PoHeaderFields {
  id: string; // uuid
  po_number: string;
  vendor_id: string;
  status: PoStatus;
  order_date: string; // YYYY-MM-DD
  quote_number: string | null;
  ship_date: string | null; // YYYY-MM-DD
  ship_via: string | null;
  terms: string | null;
  /** The payment clock, snapshotted from the vendor at creation like `terms`
   *  and soft afterwards. Null = no clock recorded for this order. The due
   *  date it produces arrives computed as `payment_clock` on the detail. */
  terms_days: number | null;
  terms_basis: PaymentTermsBasis | null;
  ship_to_name: string | null;
  ship_to_attn: string | null;
  ship_to_address: string | null;
  expected_date: string | null; // YYYY-MM-DD
  percent_received: number; // 0-100, stock lines only, capped by construction
  /** The sum of every line, charges included. Never net of the discount. */
  subtotal_cents: number;
  /** ONE manual discount per order (migration 110), on the header, drawn
   *  beneath the lines. `discount_pct` is how the buyer typed it when they typed
   *  a percent (the cents then follow the lines); null means the amount was
   *  typed in dollars and stands. 0 cents is no discount. */
  discount_cents: number;
  discount_pct: number | null;
  discount_reason: string | null;
  /** subtotal_cents - discount_cents, generated server-side. The figure the
   *  approval threshold, the approver's DM and the PDF's grand total use. */
  total_cents: number;
  over_receipt_pct: number | null; // per-PO tolerance override; null = use the global default
  on_hold: boolean;
  hold_reason: string | null;
  amended_from: string | null; // uuid of the PO this one superseded, when set
  origin: PoOrigin;
  /** INTERNAL prose. Never printed on the PDF: deny() prepends its rejection
   * reason here and the historical import stamps its own text here, so this
   * column carries things a vendor must not read. */
  notes: string | null;
  /** Written by a person, FOR THE VENDOR. The only prose column the generated
   * PDF renders (beneath the totals). Nothing automated writes it. */
  vendor_notes: string | null;
  created_by: string | null; // uuid, no name join on this surface
  submitted_at: string | null; // timestamptz
  approved_by: string | null; // uuid, no name join on this surface
  approved_at: string | null; // timestamptz
  closed_at: string | null; // timestamptz — also set on cancel, not just close
  closed_by: string | null; // uuid, no name join on this surface
  close_reason: string | null; // doubles as the cancel reason (see cancel())
  created_at: string; // timestamptz
  updated_at: string; // timestamptz
}

/**
 * WHAT THIS PO WILL ACCEPT RIGHT NOW, computed server-side by
 * `editabilityFor()` in purchase-orders-service.js — the same call `update()`
 * gates on, so the API cannot advertise a field it would then refuse.
 *
 * The frontend holds NO copy of the rule. Read these lists; never hardcode a
 * field name against them. Adding an editable field is a server change only.
 */
export interface PoEditable {
  /** Header columns writable right now. */
  fields: string[];
  /** The subset that never disturbs an approval in flight. Equal to `fields` on
   * a document that is already out the door. */
  soft_fields: string[];
  /** Whether the SHAPE of the order may change: add a line, remove one, change
   * what a line is, what it costs, or its printed description. */
  lines: boolean;
  /** The per-line keys a patch may carry right now. Every writable key while
   * `lines` is true; on an issued app order the short-fill tier
   * (`quantity_expected` down only, `expected_arrival_date`); empty once the
   * order is finished. */
  line_fields: string[];
  /** True from issue onward: lowering a quantity then needs a reason, writes
   * the line's `quantity_expected_original` once, and lands a
   * `quantity_reduced` event. Draft and pending edits are authoring. */
  quantity_reductions_recorded: boolean;
  /** True when writing outside `soft_fields` invalidates the approvers' Slack
   * messages and asks them again. */
  reasks_approval: boolean;
}

/** GET /procurement/purchase-orders row shape — header + a flat vendor
 * projection + a line count. Money fields are present today because every
 * route on this router is procurement|admin-gated already (belt-and-suspenders
 * stripping lives server-side and cannot fire yet — see the router's comment). */
export interface PoListRow extends PoHeaderFields {
  vendor_name: string | null;
  vendor_code: string | null;
  vendor_type: string | null;
  line_count: number;
  /** Returns on this order the vendor has not answered yet: claims that are
   *  open (raised, not shipped) or shipped (with the vendor). Counts of
   *  purchase_returns rows, not units. */
  returns_open: number;
  /** Returns the vendor HAS answered: replaced or refunded. Together with
   *  returns_open this is every non-cancelled, non-scrapped claim. */
  returns_settled: number;
}

export interface PoListResponse {
  rows: PoListRow[];
  page: number;
  page_size: number;
}

/** The list's returns axis. Orthogonal to `status`: a closed order can still be
 * waiting on a vendor's answer, which is exactly the row this filter exists to
 * find. `any` is "has a return at all", not "any value of this filter" — the
 * filter at rest sends nothing. */
export type PoReturnsFilter = 'any' | 'awaiting' | 'refunded' | 'replaced';

export interface PoListParams {
  /** CSV of PoStatus, e.g. 'submitted,partially_received'. Omit for all. */
  status?: string;
  vendor_id?: string;
  q?: string;
  /** Combines with status/vendor/q rather than replacing any of them. */
  returns?: PoReturnsFilter;
  page?: number;
  page_size?: number;
}

/** One purchase_order_lines row, as returned inside GET /purchase-orders/:id.
 * `notes` is the line's own free text (the backend accepts `description` as a
 * write-time alias for this same column, but read responses always say
 * `notes`). `part_description` is the CATALOG row's description, joined in
 * separately — the two can differ and both are shown on the detail page. */
export interface PoLine {
  id: string; // uuid
  purchase_order_id: string | null;
  line_number: number;
  line_type: PoLineType;
  part_catalog_id: number | null; // null for service/charge, or a stock line with no SKU yet
  revision: string | null;
  quantity_expected: number;
  /** GROSS: what physically arrived, ever. A return never lowers it, because
   *  what was delivered was delivered. */
  quantity_received: number;
  /** DERIVED server-side: the units on this line sitting on non-cancelled
   *  purchase returns. NET received is `quantity_received - quantity_returned`,
   *  and net is what the line's status and the Remaining column are built on:
   *  a replacement can be received against the same line, and a refund leaves
   *  the line short until somebody closes the order. */
  quantity_returned?: number;
  /** What the line said before its first post-issue reduction (migration 073),
   *  or null while it still says what was ordered. Written once, never
   *  overwritten; the page renders "Originally N: reason" under the quantity. */
  quantity_expected_original?: number | null;
  /** The reason for the LATEST post-issue reduction, null when there was none. */
  quantity_change_reason?: string | null;
  vendor: string | null; // free-text vendor name, carried over from the pre-009 shape
  vendor_id: string | null;
  purchase_order_ref: string | null; // legacy free-text PO ref field
  unit_cost_cents: number | null;
  line_total_cents: number; // computed server-side: quantity_expected * unit_cost_cents
  expected_arrival_date: string | null; // YYYY-MM-DD
  location_id: number | null;
  notes: string | null;
  status: PoLineStatus;
  source: PoLineSource;
  procurement_order_id: string | null; // idempotency key from the legacy hub wrapper
  created_by: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  cancelled_by: string | null;
  cancelled_reason: string | null;
  // Joined from parts_catalog / part_families:
  sku: string | null;
  part_description: string | null;
  /** The catalog row's name, joined in. Render with components/ItemName, which
   *  falls back to part_description on a line that predates the name columns. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  family_key: string | null;
  family_label: string | null;
}

/** GET /procurement/purchase-orders/:id — header + nested vendor + lines.
 * created_by_name / approved_by_name / closed_by_name are name joins done on
 * THIS endpoint only — the bare created_by/approved_by/closed_by uuids on
 * PoHeaderFields have no name join on the list row. Any of the three can be
 * null (no actor recorded, or the step hasn't happened yet); render nothing
 * rather than falling back to the uuid. */
export interface PoDetail extends PoHeaderFields {
  vendor: PoVendorSummary | null;
  lines: PoLine[];
  created_by_name: string | null;
  approved_by_name: string | null;
  closed_by_name: string | null;
  /** Detail only. The list route does not carry it — nothing on the list edits. */
  editable: PoEditable;
  /** Detail only. The order's clock resolved against its own dates, server-side. */
  payment_clock: PaymentClock;
}

// ---------------------------------------------------------------------------
// Write payloads
// ---------------------------------------------------------------------------

/** One line on create/update. Mirrors purchase-orders-service.js
 * LINE_WRITE_FIELDS + validateLine's `description` write alias for `notes`. */
export interface PoLineInput {
  id?: string; // present on an update() line patch; absent = append a new line
  part_catalog_id?: number | null;
  revision?: string | null;
  quantity_expected?: number;
  unit_cost_cents?: number | null;
  expected_arrival_date?: string | null;
  location_id?: number | null;
  notes?: string | null;
  description?: string | null; // write-time alias for notes, matches the backend
  line_type?: PoLineType;
  purchase_order_ref?: string | null;
  /** Update only. Where this line sits in the document, counting from 1. Sent
   *  for every surviving line when any line moved; the server refuses a set
   *  with a collision and settles charges to the bottom afterwards. */
  line_number?: number;
  /** Update only. Why `quantity_expected` came down on an issued order; the
   *  server refuses the reduction without it (reason_required) and writes it
   *  to the line and to the quantity_reduced event. */
  reason?: string;
}

/** POST /procurement/purchase-orders body. Exercised by
 * Procurement/PurchaseOrderForm.tsx (the /procurement/new create page). */
export interface CreatePoInput {
  vendor_id: string;
  lines: PoLineInput[];
  quote_number?: string;
  ship_via?: string;
  terms?: string;
  /** Omitted = snapshot the vendor's default. Sent (null included) = this
   *  order's own clock, which is how the create form sends an edited one. */
  terms_days?: number | null;
  terms_basis?: PaymentTermsBasis | null;
  ship_to_name?: string;
  ship_to_attn?: string;
  ship_to_address?: string;
  notes?: string;
  vendor_notes?: string;
  order_date?: string; // YYYY-MM-DD
  ship_date?: string; // YYYY-MM-DD
  expected_date?: string; // YYYY-MM-DD
  over_receipt_pct?: number;
  /** The discount, stated whole: a percent OR a number of cents, never both
   *  non-zero. Omitted = no discount. */
  discount_pct?: number | null;
  discount_cents?: number | null;
  discount_reason?: string | null;
}

/** PATCH /procurement/purchase-orders/:id body. `lines` entries WITH an `id`
 * patch that line; entries WITHOUT one append a new line; omitted lines are
 * left alone. `remove_line_ids` is the only way to delete a line, and only
 * lines with nothing received against them can be removed (line_has_receipts).
 * A patch may not leave the PO with zero lines (lines_required), counted after
 * appends — so removing everything and adding a replacement in one patch is
 * fine. Built by poEditDraft.buildPatch from the detail page's edit mode, which
 * sends ONLY the fields that actually changed. */
export interface UpdatePoInput {
  vendor_id?: string;
  quote_number?: string | null;
  ship_via?: string | null;
  terms?: string | null;
  terms_days?: number | null;
  terms_basis?: PaymentTermsBasis | null;
  ship_to_name?: string | null;
  ship_to_attn?: string | null;
  ship_to_address?: string | null;
  notes?: string | null;
  vendor_notes?: string | null;
  order_date?: string | null;
  ship_date?: string | null;
  expected_date?: string | null;
  over_receipt_pct?: number | null;
  /** Sending either money half restates the discount whole: { discount_pct: 10 }
   *  is ten percent, { discount_cents: 5000 } is fifty dollars, and
   *  { discount_cents: 0 } removes it. The reason may travel alone. */
  discount_pct?: number | null;
  discount_cents?: number | null;
  discount_reason?: string | null;
  lines?: PoLineInput[];
  remove_line_ids?: string[];
}

// ---------------------------------------------------------------------------
// Returning received goods to the vendor (vendor RMA)
// ---------------------------------------------------------------------------
//
// Two routes, one job: read what is physically still on the shelf against this
// order, then send some of it back. The unit of a return is a RECEIPT ITEM (one
// item in a box), not a PO line: a line says what was promised, an item is stock
// a person can pick up and put in a carton, and only the item knows which
// location holds it.
//
// `quantity_received` on the PO line is NEVER touched by a return. What was
// delivered stays delivered; the return lands in `quantity_returned` beside it,
// and everything that asks "is this line done" reads the difference.

/** GET /procurement/purchase-orders/:id/receipt-boxes row: one ITEM, under the
 * response's `boxes` key (the path and key predate items).
 *
 * `disposition 'accepted'` items only, ordered by line then received_at, and
 * carrying NO money fields: this read exists to pick stock, not to price it.
 *
 * `expected_receipt_id` is the PO LINE id. The column name survived a rename
 * (receipt_items.expected_receipt_id → purchase_order_lines.id) and is the FK
 * this page groups on. */
export interface PoReceiptItem {
  id: string; // uuid, the item
  receipt_box_id?: string; // uuid, the box it is in
  /** The box's sticker. */
  barcode: string;
  expected_receipt_id: string | null; // purchase_order_lines.id
  part_catalog_id: number | null;
  quantity_received: number;
  /** What is still on the shelf. An item that has been picked from, split or
   *  already returned reads lower than quantity_received. */
  quantity_remaining: number;
  location_id: number | null;
  location_name: string | null;
  lot_number: string | null;
  received_at: string | null; // timestamptz
}

/** POST /procurement/purchase-orders/:id/returns body.
 *
 * `manifest` is 'new' (start a draft for this PO's vendor) or the uuid of an
 * existing DRAFT manifest for the same vendor. `reason` is the required free
 * text, ≤ 500 chars; `reason_id` is the optional preset from
 * GET /procurement/board-returns/reasons and does not replace it.
 *
 * Nothing here says whether money is coming back. Raise time is too early to
 * know: the vendor answers that, as Replaced or Refunded, on the Returns page. */
export interface ReturnItemsInput {
  manifest: 'new' | string;
  reason_id: number | null;
  reason: string;
  lines: { receipt_item_id: string; quantity: number }[];
}

/** POST /procurement/purchase-orders/:id/returns 201 response.
 *
 * `returns` and `lines` are typed loosely on purpose. The purchase_returns row
 * and the manifest line are owned by the returns surfaces
 * (BoardReturns/boardReturnsApi.ts `ManifestLine`), and a second copy of those
 * shapes in this file is exactly how the two drift. Nothing on the PO page
 * reads past `manifest`. */
export interface ReturnItemsResult {
  ok: true;
  manifest: {
    id: string;
    manifest_number: string;
    status: string;
    vendor_id: string | null;
  };
  returns: Record<string, unknown>[];
  lines: Record<string, unknown>[];
}

/** The thing a reorder row is judged as: one part, or a whole family pooled.
 * Family members that stand in for each other are counted together, so a row is
 * listed when the target's projected stock is at or below its threshold, not
 * when its own part is. */
export interface ReorderTarget {
  /** 'f:<family_id>' when a family is pooled, 'i:<catalog_id>' for a lone part. */
  target_key: string;
  /** Set when the target is a family. */
  family_label: string | null;
  /** A family's is the sum of its watched members' thresholds. */
  threshold: number;
  /** Target-wide, not this row's part. */
  on_hand: number;
  on_order: number;
  /** What approved, not-held work orders still need. */
  committed: number;
  /** on_hand + on_order - committed. */
  projected: number;
  next_arrival: { date: string; po_number: string | null; quantity: number } | null;
  lead_time_days: number | null;
  /** YYYY-MM-DD: today plus the lead time. */
  order_today_lands: string | null;
}

/** GET /procurement/reorder-suggestions row (ERP chunk 1, phase 8, decision 10).
 * READ-ONLY report. A row is listed when its target's projected stock
 * (on_hand + on_order - committed to open work orders) is at or below the
 * target's threshold; family members are pooled into one target. Rows arrive
 * sorted by (threshold - projected), deepest shortage first. The row's own
 * on_hand, on_order and threshold stay that part's alone; `target` carries the
 * pooled figures the list is decided on. on_order already nets quantity_received
 * off pending/partial STOCK lines only (cancelled/received lines and
 * service/charge lines never contribute), and units on a REFUNDED return are
 * not in it: that money came back, so those units are not coming. suggested_vendor
 * is whichever vendor was most recently used for this SKU on any PO line, or
 * null if none ever named one — there is no notion of "the" vendor for a SKU,
 * only history. */
export interface ReorderSuggestionRow {
  part_catalog_id: number;
  sku: string;
  description: string;
  // From the catalog_item_names view (migration 068).
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  on_hand: number;
  on_order: number;
  /** Units sitting on an open or shipped return: gone off the shelf, not yet
   *  answered for. Counted as NEITHER on hand nor on order, because the vendor
   *  may send replacements or may send money, and the buyer needs to see the
   *  hole either way. */
  with_vendor: number;
  threshold: number;
  suggested_vendor: string | null;
  target: ReorderTarget;
}

// ---------------------------------------------------------------------------
// Reorder-suggestion → Draft-a-PO prefill handoff (ERP chunk 1, phase 8)
// ---------------------------------------------------------------------------
//
// The "Below reorder threshold" panel (Procurement/ReorderPanel.tsx, mounted
// on PurchaseOrderList — moved off the Receiving Hub per the design decision
// "POs on the PO page, Receiving on the Receiving page") has a "Draft a PO"
// action. It stashes the suggestion here and navigates to /procurement/new,
// which reads this key on mount and sessionStorage.removeItem()s it after
// consuming it, so a stale prefill doesn't reappear on a later visit that has
// nothing to do with it.
//
// sessionStorage (not localStorage): the prefill is a one-shot handoff for
// THIS tab's navigation, not a value that should survive a new tab or a week
// from now.
/** A line duplicate() left off because its catalog item is retired. */
export interface RetiredLine {
  line_number: number;
  sku: string | null;
}

export const REORDER_PREFILL_SESSION_KEY = 'po.draft.prefill';

export interface ReorderDraftPrefill {
  sku: string;
  part_catalog_id: number;
  /** threshold-based suggestion: enough to bring on_hand + on_order back up
   * to the SKU's reorder_threshold, floored at 1. A starting point for the
   * buyer to edit, not a purchasing decision. */
  quantity: number;
  vendor_name: string | null;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Every failing response on this router is { error: { code, message, ...} }.
 * apiHelpers attaches the parsed body to err.data and the message to
 * err.message, so callers do: err?.data?.error?.message || err?.message. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    [key: string]: unknown;
  };
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

export function usePoApi() {
  const api = useApi();

  return {
    list: (params: PoListParams = {}): Promise<PoListResponse> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.vendor_id) qs.set('vendor_id', params.vendor_id);
      if (params.q) qs.set('q', params.q);
      if (params.returns) qs.set('returns', params.returns);
      if (params.page) qs.set('page', String(params.page));
      if (params.page_size) qs.set('page_size', String(params.page_size));
      const suffix = qs.toString();
      return api.get(`/procurement/purchase-orders${suffix ? `?${suffix}` : ''}`);
    },

    get: (id: string): Promise<PoDetail> => api.get(`/procurement/purchase-orders/${id}`),

    // Not called by any page yet (see CreatePoInput doc comment).
    create: (payload: CreatePoInput): Promise<PoDetail> =>
      api.post('/procurement/purchase-orders', { body: payload }),

    update: (id: string, patch: UpdatePoInput): Promise<PoDetail> =>
      api.patch(`/procurement/purchase-orders/${id}`, { body: patch }),

    submit: (id: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/submit`),

    /** reason is optional — the router only rejects a MISSING reason on
     * cancel() AND on close(). Omit the body key entirely when blank so an
     * empty string doesn't get stored as "the" close reason. */
    close: (id: string, reason?: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/close`, {
        body: reason ? { reason } : {},
      }),

    cancel: (id: string, reason: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/cancel`, { body: { reason } }),

    /** Closed orders only; the server 409s anything else (`not_closed`).
     * Reason required, like close and cancel. */
    reopen: (id: string, reason: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/reopen`, { body: { reason } }),

    amend: (id: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/amend`),

    /** A NEW draft to the same vendor with the same lines; the original is left
     * alone. Not a revision: it gets its own number, today's date, and no link
     * back except the copy's own `created` event. */
    duplicate: (id: string): Promise<PoDetail & { skipped_retired: RetiredLine[] }> =>
      api.post(`/procurement/purchase-orders/${id}/duplicate`),

    // Approval-flow routes (phase 5). Live: approve() succeeds for a
    // configured approver (or any admin in bootstrap mode, see
    // Admin/ApprovalSettings.tsx) and 403s everyone else on a
    // pending_approval PO; the detail page just surfaces whatever message
    // the API returns.
    approve: (id: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/approve`),

    deny: (id: string, reason: string): Promise<PoDetail> =>
      api.post(`/procurement/purchase-orders/${id}/deny`, { body: { reason } }),

    /** What is still on the shelf against this order, item by item. Feeds the
     *  Return items dialog; see PoReceiptItem for why the item and not the line
     *  is the unit. */
    receiptBoxes: (id: string): Promise<{ boxes: PoReceiptItem[] }> =>
      api.get(`/procurement/purchase-orders/${id}/receipt-boxes`),

    /** Sends stock back to the vendor: one transaction that moves every named
     *  quantity to QC hold, opens a purchase return per item, and puts them all
     *  on one shipping manifest. */
    returnItems: (id: string, input: ReturnItemsInput): Promise<ReturnItemsResult> =>
      api.post(`/procurement/purchase-orders/${id}/returns`, { body: input }),

    // Read-only reorder report (phase 8, decision 10). See ReorderSuggestionRow.
    reorderSuggestions: (): Promise<{ rows: ReorderSuggestionRow[] }> =>
      api.get('/procurement/reorder-suggestions'),
  };
}

// ---------------------------------------------------------------------------
// Display helpers — money, statuses, badges. One copy so list + detail can
// never drift on what a chip or a dollar figure looks like.
// ---------------------------------------------------------------------------

/** Integer cents → dollar string. Matches the codebase's existing no-comma
 * convention (Receiving/ReceiptItemDetail.tsx, Parts/PartsCatalogPage.tsx).
 * Also takes the bigint-as-string shape AP columns cross the wire in, so the
 * match board can format both of its sides through this one seam. */
export function formatCents(cents: number | string | null | undefined): string {
  if (cents === null || cents === undefined || cents === '') return '—';
  const n = typeof cents === 'string' ? Number(cents) : cents;
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${(Math.abs(n) / 100).toFixed(2)}`;
}

/** Integer cents → a bare dollar NUMBER, for Export CSV. The sibling of
 * formatCents, and here for the same reason: this module is the only place
 * cents become dollars. A spreadsheet wants a number it can sum, so this one
 * carries no currency sign and no fixed decimals, and an absent figure stays
 * empty (null) rather than becoming a zero somebody would then add up. */
export function centsToDollars(cents: number | string | null | undefined): number | null {
  if (cents === null || cents === undefined || cents === '') return null;
  const n = typeof cents === 'string' ? Number(cents) : cents;
  return Number.isFinite(n) ? n / 100 : null;
}

/** Dollars string (as typed) → integer cents, the shape every write payload
 * wants. Blank or unparseable is null, which the API reads as "no cost". */
export function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Math.round(parseFloat(trimmed) * 100);
  return Number.isFinite(n) ? n : null;
}

/** Cents → the string a money <input> should start with. Unlike formatCents
 * there is no $ and no em-dash: this feeds a text field, not a reader. */
export function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/** Path for the on-demand PDF render (phase 7, api/src/procurement/po-pdf-service.js).
 * The route is JWT-gated like every other PO read, so a bare `<a href>` or
 * `window.open` won't carry auth — open it with `openAuthedPdf` from
 * `Receiving/openAuthedPdf.ts`, the same helper the label-PDF pages use. */
export function pdfUrl(id: string): string {
  return `/api/procurement/purchase-orders/${id}/pdf`;
}

export const PO_STATUS_LABELS: Record<PoStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  submitted: 'Submitted',
  partially_received: 'Partially received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

// bg/text/ring triad, matching the STATUS_COLORS idiom in Receiving/ReceivingHubPage.tsx.
// draft gray, pending_approval amber, submitted blue, partially_received primary,
// received green, closed neutral (slate, distinct from draft's gray), cancelled dim red.
export const PO_STATUS_COLORS: Record<PoStatus, string> = {
  draft: 'bg-[rgb(95_95_95/0.15)] text-on-surface-variant ring-[rgb(95_95_95/0.30)]',
  pending_approval: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  submitted: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
  partially_received: 'bg-primary/15 text-primary-ink ring-primary/40',
  received: 'bg-green-500/15 text-green-300 ring-green-500/30',
  closed: 'bg-slate-500/15 text-slate-400 ring-slate-500/30',
  cancelled: 'bg-red-500/10 text-red-400/70 ring-red-500/20',
};

export const PO_LINE_STATUS_LABELS: Record<PoLineStatus, string> = {
  pending: 'Pending',
  partial: 'Partial',
  received: 'Received',
  cancelled: 'Cancelled',
};

// Same palette family as the header chips, reused at the line level.
export const PO_LINE_STATUS_COLORS: Record<PoLineStatus, string> = {
  pending: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
  partial: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  received: 'bg-green-500/15 text-green-300 ring-green-500/30',
  cancelled: 'bg-[rgb(95_95_95/0.15)] text-on-surface-variant ring-[rgb(95_95_95/0.30)]',
};

export const PO_LINE_TYPE_LABELS: Record<PoLineType, string> = {
  stock: 'Stock',
  service: 'Service',
  charge: 'Charge',
};

export const PO_LINE_TYPE_COLORS: Record<PoLineType, string> = {
  stock: 'bg-gray-700/60 text-on-surface-variant ring-[rgb(70_70_70/0.40)]',
  service: 'bg-indigo-500/15 text-indigo-300 ring-indigo-500/30',
  charge: 'bg-teal-500/15 text-teal-300 ring-teal-500/30',
};

/** "Open" groups submitted + partially_received for the list's status tabs —
 * both mean "sent to the vendor, not done yet," and splitting them into two
 * tabs would just make the common case two clicks instead of one. */
export const PO_OPEN_STATUSES: PoStatus[] = ['submitted', 'partially_received'];

/** Statuses where a document is finished and takes no further actions. */
export function isTerminalPoStatus(status: PoStatus): boolean {
  return status === 'closed' || status === 'cancelled';
}
